import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Navigate } from "react-router-dom";
import { membershipConfig, formatMembership, getMembershipConfig, normaliseTier } from "@/lib/membershipUtils";

export default function Members() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: members = [] } = useQuery({
    queryKey: ["members"],
    queryFn: () => base44.entities.User.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.User.update(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["members"] }),
  });

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold tracking-tight">Members</h1>
        <p className="text-muted-foreground mt-1">Manage member accounts and memberships</p>
      </div>

      <div className="grid gap-3">
        {members.length === 0 ? (
          <Card className="p-12 text-center border-0 shadow-sm">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No members yet</p>
          </Card>
        ) : (
          members.map((member) => (
            <Card key={member.id} className="p-4 border-0 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                  {(member.full_name || member.email || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{member.full_name || "—"}</p>
                  <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="secondary" className={getMembershipConfig(member.membership_type).className}>
                    {formatMembership(member.membership_type)}
                  </Badge>
                  <Select
                    value={normaliseTier(member.membership_type)}
                    onValueChange={(val) => updateMutation.mutate({ id: member.id, data: { membership_type: val } })}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="foundation">Foundation</SelectItem>
                      <SelectItem value="momentum">Momentum</SelectItem>
                      <SelectItem value="strength">Strength</SelectItem>
                      <SelectItem value="elite_coaching">Elite Coaching</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}